import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMFAStatus, disableMFA } from '../../services/api';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { MFASetup } from './MFASetup';

export const MFAStatus = () => {
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const { data: mfaStatus, isLoading } = useQuery({
    queryKey: ['mfaStatus'],
    queryFn: getMFAStatus,
  });

  const disableMutation = useMutation({
    mutationFn: (code: string) => disableMFA(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfaStatus'] });
      setDisableDialogOpen(false);
      setDisableCode('');
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    },
  });

  const handleDisable = () => {
    if (disableCode.length !== 6) {
      setError('Code must be 6 digits');
      return;
    }
    disableMutation.mutate(disableCode);
  };

  const handleSetupSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['mfaStatus'] });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <SecurityIcon color={mfaStatus?.mfa_enabled ? 'success' : 'action'} />
              <Typography variant="h6">Two-Factor Authentication</Typography>
            </Box>
            {mfaStatus?.mfa_enabled ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Enabled"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<WarningIcon />}
                label="Disabled"
                color="warning"
                size="small"
              />
            )}
          </Box>

          {mfaStatus?.mfa_enabled ? (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Your account is protected with two-factor authentication using an authenticator app.
              </Typography>

              <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    TOTP Configured:
                  </Typography>
                  <Typography variant="body2">
                    {mfaStatus.totp_configured ? 'Yes' : 'No'}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Backup Codes Remaining:
                  </Typography>
                  <Typography variant="body2">
                    {mfaStatus.backup_codes_remaining}
                  </Typography>
                </Box>

                {mfaStatus.enabled_at && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Enabled:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(mfaStatus.enabled_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {mfaStatus.backup_codes_remaining <= 3 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You have {mfaStatus.backup_codes_remaining} backup codes remaining.
                  Consider regenerating them soon.
                </Alert>
              )}

              <Button
                variant="outlined"
                color="error"
                onClick={() => setDisableDialogOpen(true)}
                fullWidth
              >
                Disable Two-Factor Authentication
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Add an extra layer of security to your account. You'll need to enter a code from
                your authenticator app each time you log in.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Recommended:</strong> Enable MFA to protect your account from unauthorized access.
                </Typography>
              </Alert>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setSetupDialogOpen(true)}
                fullWidth
                startIcon={<SecurityIcon />}
              >
                Enable Two-Factor Authentication
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* MFA Setup Dialog */}
      <MFASetup
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={handleSetupSuccess}
      />

      {/* Disable MFA Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling MFA will make your account less secure. You'll only need your password to log in.
          </Alert>

          <Typography variant="body2" gutterBottom>
            Enter the 6-digit code from your authenticator app to confirm:
          </Typography>

          <TextField
            fullWidth
            label="Verification Code"
            value={disableCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setDisableCode(value);
            }}
            placeholder="123456"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
              inputMode: 'numeric',
              style: { fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' },
            }}
            sx={{ my: 2 }}
            autoFocus
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDisableDialogOpen(false);
              setDisableCode('');
              setError('');
            }}
            disabled={disableMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisable}
            color="error"
            variant="contained"
            disabled={disableCode.length !== 6 || disableMutation.isPending}
          >
            {disableMutation.isPending ? 'Disabling...' : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
